import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { sortFnByKey } from "@notemeal/utils/sort";
import React, { useState } from "react";
import { getMuiTablePaginationProps, getRowsForTablePage, useOffsetPagination } from "../../utils/pagination";

export interface IFoodGroup {
  foodCount: number;
  id: string;
  name: string;
  source: string | null;
  foods: readonly { id: string; name: string }[];
}

interface FoodGroupTableProps {
  rows: IFoodGroup[];
  setSelectedFoodGroup: (foodGroup: IFoodGroup | null) => void;
  setFoodGroupModalOpen: (open: boolean) => void;
}

interface ReadOnlyFoodGroupTableProps extends FoodGroupTableProps {
  readOnly: boolean;
}

const isReadOnly = (props: FoodGroupTableProps | ReadOnlyFoodGroupTableProps): props is ReadOnlyFoodGroupTableProps => {
  return (props as ReadOnlyFoodGroupTableProps).readOnly !== undefined;
};

export const FoodGroupTable = (props: ReadOnlyFoodGroupTableProps | FoodGroupTableProps) => {
  const { setFoodGroupModalOpen, setSelectedFoodGroup, rows: foodGroups } = props;
  const ROWS_PER_PAGE = 15;
  const pagination = useOffsetPagination(ROWS_PER_PAGE);
  const { limit, page, queryText, onChangeQueryText } = pagination;
  const [sortOrderAscending, setSortOrderAscending] = useState(true);

  const getRows = () => foodGroups.sort(sortFnByKey("name", { reverse: !sortOrderAscending })).filter(filterRow) || [];
  const filterRow = (row: IFoodGroup) => row.name.toLowerCase().indexOf(queryText.toLowerCase()) !== -1;
  const getSortOrder = () => (sortOrderAscending ? "asc" : "desc");
  const toggleSortOrder = () => setSortOrderAscending(!sortOrderAscending);

  const rows = getRows();
  const pageRows = getRowsForTablePage({ rows, limit, page });

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", alignItems: "flex-end" }}>
        <TextField
          label="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          value={queryText}
          onChange={e => onChangeQueryText(e.target.value)}
        />
        <Button disabled={isReadOnly(props)} onClick={() => setFoodGroupModalOpen(true)}>
          Add Food Group
        </Button>
      </Box>
      <TableContainer sx={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 300 }}>
                <TableSortLabel direction={getSortOrder()} onClick={toggleSortOrder}>
                  <b>Name</b>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <b># Foods</b>
              </TableCell>
              {isReadOnly(props) && (
                <TableCell>
                  <b>Source</b>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map(foodGroup => {
              const { id, name, foods, source } = foodGroup;
              return (
                <TableRow
                  id={id}
                  onClick={() => {
                    setSelectedFoodGroup(foodGroup);
                    setFoodGroupModalOpen(true);
                  }}
                >
                  <TableCell>
                    <Typography>{name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{foods.length}</Typography>
                  </TableCell>
                  {isReadOnly(props) && (
                    <TableCell>
                      <Typography>{source}</Typography>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        sx={{ ".MuiTablePagination-actions": { mr: 8 } }}
        {...getMuiTablePaginationProps(pagination, rows.length)}
      />
    </Box>
  );
};
